// DS — JEDYNY przycisk (artboard 02, G3-02, G3-03, G7-12).
//
// Trzy role × dwa rozmiary × sześć stanów:
//   primary   — gradient marki; jeden na ekran
//   secondary — obramowanie --border-hover, tło karty (domyślny)
//   quiet     — bez obramowania, kolor linku
//   tone="danger" — drugorzędny kształt, tekst --danger-fg (nigdy pełne wypełnienie)
//   stany: domyślny · hover · wciśnięty (scale .98) · fokus · wyłączony z powodem · w toku
//
// Dawne nazwy wariantów (`grad`, `ghost`, `text`, `danger`, `danger-ghost`) są
// przyjmowane i mapowane, żeby 250 ręcznych wywołań w ekranach mogło się
// przepinać ekran po ekranie (paczki 07–11); CSS zna obie rodziny klas.
//
// `disabledReason`: wyłączony przycisk ZAWSZE niesie zdanie z powodem obok
// (G2-10, G7-13) — renderuje się pod przyciskiem i jest jego aria-describedby.
// `busy`: spinner 16 w miejscu tekstu, przycisk nieaktywny, aria-busy.
// `to` / `href`: ten sam przycisk jako link (G3-30).

import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react'
import { useId } from 'react'
import { Link } from 'react-router'

export type ButtonVariant = 'primary' | 'secondary' | 'quiet'
type LegacyVariant = 'grad' | 'ghost' | 'text' | 'danger' | 'danger-ghost'

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  quiet: 'btn-quiet',
}

function normalise(v: ButtonVariant | LegacyVariant | undefined): { variant: ButtonVariant; danger: boolean } {
  switch (v) {
    case 'grad': return { variant: 'primary', danger: false }
    case 'ghost': return { variant: 'secondary', danger: false }
    case 'text': return { variant: 'quiet', danger: false }
    case 'danger':
    case 'danger-ghost': return { variant: 'secondary', danger: true }
    case undefined: return { variant: 'secondary', danger: false }
    default: return { variant: v, danger: false }
  }
}

type Common = {
  variant?: ButtonVariant | LegacyVariant
  /** `danger` = akcja niszcząca: drugorzędny kształt + tekst --danger-fg. */
  tone?: 'default' | 'danger'
  /** 44 (domyślny) albo 36 (w wierszu, w karcie). `lg` = dawny trzeci rozmiar, dziś = md. */
  size?: 'md' | 'sm' | 'lg'
  /** Pełna szerokość. */
  block?: boolean
  /** W toku: spinner zamiast tekstu, przycisk nieaktywny. */
  busy?: boolean
  /** Zdanie, DLACZEGO przycisk jest wyłączony. Ustawia disabled i renderuje powód pod przyciskiem. */
  disabledReason?: string
  className?: string
  children?: ReactNode
}

type AsButton = Common & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'> & { to?: undefined; href?: undefined }
type AsLink = Common & { to: string; href?: undefined } & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'children' | 'href'>
type AsAnchor = Common & { href: string; to?: undefined } & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'children'>

export type ButtonProps = AsButton | AsLink | AsAnchor

export function Button(props: ButtonProps) {
  const {
    variant: rawVariant, tone = 'default', size = 'md', block, busy, disabledReason, className = '', children,
    ...rest
  } = props as Common & Record<string, unknown>
  const { variant, danger } = normalise(rawVariant)
  const reasonId = useId()
  const isDanger = danger || tone === 'danger'

  const cls = [
    'btn',
    VARIANT_CLASS[variant],
    isDanger && 'btn-danger',
    size === 'sm' && 'btn-sm',
    block && 'btn-block',
    busy && 'is-busy',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  // The reason wins when there is one; otherwise the caller's own `aria-describedby`
  // (e.g. one shared sentence under a row of blocked actions) is kept.
  const describedBy = disabledReason ? reasonId : (rest as { 'aria-describedby'?: string })['aria-describedby']

  let control: ReactNode
  if ('to' in props && props.to) {
    const { to, ...a } = rest as unknown as { to: string } & AnchorHTMLAttributes<HTMLAnchorElement>
    control = (
      <Link to={to} className={cls} aria-busy={busy || undefined} aria-describedby={describedBy}
            aria-disabled={disabledReason || busy ? true : undefined} {...a}>
        {children}
      </Link>
    )
  } else if ('href' in props && props.href) {
    const { href, ...a } = rest as unknown as { href: string } & AnchorHTMLAttributes<HTMLAnchorElement>
    control = (
      <a href={href} className={cls} aria-busy={busy || undefined} aria-describedby={describedBy}
         aria-disabled={disabledReason || busy ? true : undefined} {...a}>
        {children}
      </a>
    )
  } else {
    const b = rest as ButtonHTMLAttributes<HTMLButtonElement>
    control = (
      <button
        type={b.type ?? 'button'}
        {...b}
        className={cls}
        disabled={b.disabled || busy || Boolean(disabledReason)}
        aria-busy={busy || undefined}
        aria-describedby={describedBy}
      >
        {children}
      </button>
    )
  }

  if (!disabledReason) return control
  return (
    <span className={block ? 'btn-wrap btn-wrap-block' : 'btn-wrap'}>
      {control}
      <span className="btn-reason" id={reasonId}>{disabledReason}</span>
    </span>
  )
}

export default Button
